/**
 * Builds HTML for a payment receipt (recibo de pago/abono).
 * Used when a partial payment is registered on an invoice.
 */
import type { Factura } from '../components/facturas/types';
import { numeroFacturaParaMostrar } from './facturaNumeroCanal';
import { metodoPagoFacturaLabel } from './facturaPdfHtml';

function escHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface ReciboPagoData {
  pagoId: string;
  monto: number;
  metodoPago: string;
  fecha: Date;
  saldoAnterior: number;
  saldoDespues: number;
  notas?: string;
  numeroRecibo?: string;
}

export interface ReciboPdfCompanyInfo {
  companyName: string;
  companyLogo: string | null;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  companyNit?: string;
}

export function buildReciboPagoPdfHtml(
  factura: Factura,
  pago: ReciboPagoData,
  info: ReciboPdfCompanyInfo
): string {
  const {
    companyName,
    companyLogo,
    companyEmail,
    companyPhone,
    companyAddress,
    companyNit,
  } = info;

  const numeroMostrar = numeroFacturaParaMostrar(factura.numero, factura.canalVenta);
  const numeroRecibo = pago.numeroRecibo || `REC-${pago.pagoId.slice(0, 8).toUpperCase()}`;

  const formatDate = (d: Date) => (d instanceof Date ? d : new Date(d)).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const formatDateTime = (d: Date) => {
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const formatNum = (n: number) => n.toLocaleString('es-CO');

  const clienteNombre = factura.cliente
    ? `${factura.cliente.nombre || ''} ${(factura.cliente as { apellido?: string })?.apellido || ''}`.trim() || 'Sin nombre'
    : 'Sin cliente';
  const clienteDocumento = factura.cliente?.numeroDocumento || '';
  const clienteTelefono = factura.cliente?.telefono || '';
  const clienteEmail = factura.cliente?.email || '';

  return `<!DOCTYPE html>
<html>
  <head>
    <title>Recibo de Pago ${numeroRecibo} - ${companyName}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: #f8fafc; }
      .receipt { max-width: 600px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden; }
      .header { background: linear-gradient(135deg, #0B1F4A 0%, #1A56DB 100%); color: white; padding: 24px; }
      .header-content { display: flex; align-items: center; gap: 16px; }
      .company-logo { max-height: 50px; max-width: 150px; border-radius: 8px; background: white; padding: 4px; }
      .company-logo-placeholder { height: 50px; width: 50px; background: white; color: #0B1F4A; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: bold; }
      .header-text h1 { font-size: 22px; margin: 0 0 4px; font-weight: 600; }
      .header-text p { font-size: 12px; margin: 0; opacity: 0.9; }
      .badge { display: inline-block; background: #10b981; color: white; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; margin-top: 8px; }
      .content { padding: 24px; }
      .receipt-number { font-size: 13px; color: #6b7280; margin-bottom: 16px; }
      .receipt-number strong { color: #1f2937; }
      .amount-box { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border: 2px solid #22c55e; border-radius: 12px; padding: 20px; text-align: center; margin-bottom: 20px; }
      .amount-label { font-size: 13px; color: #166534; margin-bottom: 4px; font-weight: 500; }
      .amount-value { font-size: 32px; font-weight: 700; color: #15803d; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
      .info-box { background: #f9fafb; border-radius: 8px; padding: 12px; }
      .info-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
      .info-value { font-size: 14px; color: #1f2937; font-weight: 500; }
      .divider { border: 0; border-top: 1px solid #e5e7eb; margin: 20px 0; }
      .section-title { font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
      .balance-summary { background: #f8fafc; border-radius: 8px; padding: 16px; }
      .balance-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
      .balance-row.total { border-top: 2px solid #e5e7eb; margin-top: 8px; padding-top: 12px; font-weight: 600; }
      .balance-row .label { color: #6b7280; }
      .balance-row .value { color: #1f2937; font-weight: 500; }
      .balance-row.pending .value { color: #ea580c; font-weight: 600; }
      .balance-row.paid .value { color: #16a34a; font-weight: 600; }
      .client-info { margin-bottom: 20px; }
      .client-name { font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 4px; }
      .client-details { font-size: 13px; color: #6b7280; }
      .invoice-ref { background: #eff6ff; border-left: 3px solid #3b82f6; padding: 12px; border-radius: 0 8px 8px 0; margin-bottom: 20px; }
      .invoice-ref-label { font-size: 11px; color: #1d4ed8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
      .invoice-ref-value { font-size: 14px; color: #1e40af; font-weight: 600; }
      .footer { background: #f9fafb; padding: 20px 24px; text-align: center; font-size: 12px; color: #6b7280; border-top: 1px solid #e5e7eb; }
      .footer p { margin: 4px 0; }
      .notes { background: #fefce8; border: 1px solid #fde047; border-radius: 8px; padding: 12px; margin-top: 16px; font-size: 13px; color: #854d0e; }
      .notes-label { font-weight: 600; margin-bottom: 4px; }
      @media print { 
        body { margin: 0; padding: 10px; background: white; }
        .receipt { box-shadow: none; }
      }
    </style>
  </head>
  <body>
    <div class="receipt">
      <div class="header">
        <div class="header-content">
          ${companyLogo 
            ? `<img src="${companyLogo}" alt="${escHtml(companyName)}" class="company-logo" />` 
            : `<div class="company-logo-placeholder">${escHtml(companyName.charAt(0).toUpperCase())}</div>`}
          <div class="header-text">
            <h1>${escHtml(companyName)}</h1>
            <p>Recibo de Pago</p>
            ${companyNit ? `<p>NIT: ${escHtml(companyNit)}</p>` : ''}
          </div>
        </div>
        <div class="badge">✓ Pago Recibido</div>
      </div>
      
      <div class="content">
        <div class="receipt-number">
          <strong>Recibo N°:</strong> ${escHtml(numeroRecibo)}
          <span style="float: right; color: #9ca3af; font-size: 12px;">${formatDateTime(pago.fecha)}</span>
        </div>
        
        <div class="amount-box">
          <div class="amount-label">Monto Recibido</div>
          <div class="amount-value">$${formatNum(pago.monto)}</div>
        </div>
        
        <div class="info-grid">
          <div class="info-box">
            <div class="info-label">Método de Pago</div>
            <div class="info-value">${escHtml(metodoPagoFacturaLabel(pago.metodoPago) || pago.metodoPago || 'No especificado')}</div>
          </div>
          <div class="info-box">
            <div class="info-label">Fecha de Pago</div>
            <div class="info-value">${formatDate(pago.fecha)}</div>
          </div>
        </div>
        
        <div class="invoice-ref">
          <div class="invoice-ref-label">Aplicado a Factura</div>
          <div class="invoice-ref-value">${escHtml(numeroMostrar)}</div>
        </div>
        
        <hr class="divider" />
        
        <div class="section-title">
          <span>👤</span> Información del Cliente
        </div>
        <div class="client-info">
          <div class="client-name">${escHtml(clienteNombre)}</div>
          <div class="client-details">
            ${clienteDocumento ? `<p>Documento: ${escHtml(clienteDocumento)}</p>` : ''}
            ${clienteTelefono ? `<p>Teléfono: ${escHtml(clienteTelefono)}</p>` : ''}
            ${clienteEmail ? `<p>Email: ${escHtml(clienteEmail)}</p>` : ''}
          </div>
        </div>
        
        <hr class="divider" />
        
        <div class="section-title">
          <span>💰</span> Resumen de Cuenta
        </div>
        <div class="balance-summary">
          <div class="balance-row">
            <span class="label">Total de la factura</span>
            <span class="value">$${formatNum(factura.total)}</span>
          </div>
          <div class="balance-row">
            <span class="label">Saldo anterior</span>
            <span class="value">$${formatNum(pago.saldoAnterior)}</span>
          </div>
          <div class="balance-row paid">
            <span class="label">Este pago</span>
            <span class="value">- $${formatNum(pago.monto)}</span>
          </div>
          <div class="balance-row total ${pago.saldoDespues > 0 ? 'pending' : 'paid'}">
            <span class="label">${pago.saldoDespues > 0 ? 'Saldo pendiente' : 'Cuenta saldada'}</span>
            <span class="value">$${formatNum(pago.saldoDespues)}</span>
          </div>
        </div>
        
        ${pago.notas ? `
        <div class="notes">
          <div class="notes-label">📝 Notas:</div>
          ${escHtml(pago.notas)}
        </div>
        ` : ''}
      </div>
      
      <div class="footer">
        <p><strong>¡Gracias por su pago!</strong></p>
        <p>Este documento es un comprobante de pago válido.</p>
        ${companyEmail ? `<p>Email: ${escHtml(companyEmail)}</p>` : ''}
        ${companyPhone ? `<p>Teléfono: ${escHtml(companyPhone)}</p>` : ''}
        ${companyAddress ? `<p>${escHtml(companyAddress)}</p>` : ''}
      </div>
    </div>
  </body>
</html>`;
}

export function generateReciboNumero(pagoId: string): string {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const shortId = pagoId.slice(0, 6).toUpperCase();
  return `REC-${year}${month}${day}-${shortId}`;
}
